/**
 * Tiny SWR-style GET cache on top of axios.
 *
 * Why: on mobile networks a full round-trip on every navigation makes the
 * site feel sluggish. This cache serves data from memory instantly and then
 * revalidates in the background, so back / forward and inter-page clicks
 * feel free.
 *
 * Scope: only public GETs. Admin flows still use plain `api`.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "./api";

const cache = new Map();        // url -> { data, ts }
const inflight = new Map();     // url -> Promise (dedupes concurrent revalidations)
const subscribers = new Map();  // url -> Set<fn>

const STALE_MS = 20_000;        // consider data fresh for 20s; still revalidate on mount

function notify(url) {
  const subs = subscribers.get(url);
  if (subs) subs.forEach((fn) => fn(cache.get(url)?.data));
}

async function revalidate(url) {
  if (inflight.has(url)) return inflight.get(url);
  const p = api
    .get(url)
    .then((r) => {
      cache.set(url, { data: r.data, ts: Date.now() });
      notify(url);
      return r.data;
    })
    .catch(() => {
      // swallow — hook keeps previous data
    })
    .finally(() => inflight.delete(url));
  inflight.set(url, p);
  return p;
}

/** Kick off a network fetch for `url` without waiting. Safe to call many times. */
export function prefetch(url) {
  const entry = cache.get(url);
  if (entry && Date.now() - entry.ts < STALE_MS) return;
  revalidate(url);
}

/** Fire-and-forget prefetch for a list of URLs, spaced out so we don't
 *  hammer the browser's HTTP/2 concurrency limit on slow phones. */
export function prefetchMany(urls, spacingMs = 40) {
  urls.forEach((u, i) => setTimeout(() => prefetch(u), i * spacingMs));
}

/**
 * useSWR-lite: returns { data, loading } for a GET url. Data from cache is
 * returned on the first render (no loading flicker on repeat visits), and a
 * background revalidation runs on mount / when the tab regains focus.
 */
export function useApiGet(url) {
  const cached = url ? cache.get(url)?.data : undefined;
  const [data, setData] = useState(cached);
  const [loading, setLoading] = useState(cached === undefined);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (!url) return;

    // Subscribe to updates from other components requesting the same URL.
    const subs = subscribers.get(url) || new Set();
    const listener = (next) => {
      if (!mountedRef.current) return;
      setData(next);
      setLoading(false);
    };
    subs.add(listener);
    subscribers.set(url, subs);

    // If we already have cached data, don't show a spinner; just revalidate.
    const c = cache.get(url);
    if (c) {
      setData(c.data);
      setLoading(false);
    } else {
      setLoading(true);
    }
    revalidate(url);

    const onFocus = () => revalidate(url);
    window.addEventListener("focus", onFocus);

    return () => {
      mountedRef.current = false;
      subs.delete(listener);
      if (subs.size === 0) subscribers.delete(url);
      window.removeEventListener("focus", onFocus);
    };
  }, [url]);

  return { data, loading, refresh: () => (url ? revalidate(url) : null) };
}

/** Manually seed the cache — useful when parent already has the data. */
export function setCached(url, data) {
  cache.set(url, { data, ts: Date.now() });
  notify(url);
}
